import { RefObject, useCallback, useRef, useState } from "react";
import { useGesture } from "@use-gesture/react";

const MIN_SCALE = 1;
const MAX_SCALE = 4;

type TTransform = { scale: number; x: number; y: number };

const INITIAL_TRANSFORM: TTransform = { scale: MIN_SCALE, x: 0, y: 0 };

/** Max pan offset per axis so a zoomed element (transform-origin center) never leaves its own frame */
const getPanLimit = (el: HTMLElement | null, scale: number) => ({
  x: el ? (el.offsetWidth * (scale - 1)) / 2 : 0,
  y: el ? (el.offsetHeight * (scale - 1)) / 2 : 0,
});

/**
 * Untransformed center of `el` in viewport coords. Uses offset* (layout, ignores CSS transform) instead of
 * getBoundingClientRect on `el`, which can lag one frame behind the latest transform during a fast pinch.
 */
const getLayoutCenter = (el: HTMLElement) => {
  const parentRect = (el.offsetParent ?? document.body).getBoundingClientRect();
  return { x: parentRect.left + el.offsetLeft + el.offsetWidth / 2, y: parentRect.top + el.offsetTop + el.offsetHeight / 2 };
};

const clamp = (value: number, limit: number) => Math.max(-limit, Math.min(limit, value));

/**
 * Pinch-to-zoom + drag-to-pan for a single element (e.g. image preview).
 *
 * - **2-finger pinch** → zoom 1x–4x, anchored at the pinch midpoint so the content under the fingers stays put
 * - **1-finger drag** while zoomed → pan, bounded to the element's frame
 *
 * `lastGestureEndRef` holds the timestamp of the last gesture end so callers can
 * ignore the synthetic click that browsers fire right after a drag/pinch.
 * State starts fresh on mount — mount the consumer only while the preview is open.
 */
export const usePinchZoomPan = (targetRef: RefObject<HTMLElement | null>) => {
  const [transform, setTransform] = useState<TTransform>(INITIAL_TRANSFORM);
  // Mirror of state readable inside gesture callbacks without stale closures
  const transformRef = useRef(transform);
  const lastGestureEndRef = useRef(0);

  const update = useCallback((next: TTransform) => {
    transformRef.current = next;
    setTransform(next);
  }, []);

  useGesture(
    {
      onPinch: ({ offset: [scale], origin: [ox, oy] }) => {
        const el = targetRef.current;
        const prev = transformRef.current;
        if (!el || scale === prev.scale) return;

        // Shift translate so the pinch origin maps to the same content point after rescaling.
        // With transform-origin center, the visual center = untransformed layout center + translate (prev.x/y).
        const center = getLayoutCenter(el);
        const ratio = 1 - scale / prev.scale;
        const x = prev.x + (ox - (center.x + prev.x)) * ratio;
        const y = prev.y + (oy - (center.y + prev.y)) * ratio;

        const limit = getPanLimit(el, scale);
        update({ scale, x: clamp(x, limit.x), y: clamp(y, limit.y) });
      },
      onPinchEnd: () => {
        lastGestureEndRef.current = Date.now();
      },
      onDrag: ({ offset: [x, y], pinching, cancel }) => {
        if (pinching) return cancel();
        const { scale } = transformRef.current;
        if (scale <= MIN_SCALE) return;
        update({ scale, x, y });
      },
      onDragEnd: ({ tap }) => {
        if (!tap) lastGestureEndRef.current = Date.now();
      },
    },
    {
      target: targetRef,
      eventOptions: { passive: false },
      pinch: {
        scaleBounds: { min: MIN_SCALE, max: MAX_SCALE },
        from: () => [transformRef.current.scale, 0],
        pointer: { touch: true },
      },
      drag: {
        from: () => [transformRef.current.x, transformRef.current.y],
        bounds: () => {
          const limit = getPanLimit(targetRef.current, transformRef.current.scale);
          return { left: -limit.x, right: limit.x, top: -limit.y, bottom: limit.y };
        },
        filterTaps: true,
        pointer: { touch: true },
      },
    },
  );

  return { transform, lastGestureEndRef };
};
